import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  Phone as PhoneIcon,
  Email as MailIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { invoiceTemplateOptions } from '../../../utils/invoiceTemplates';

interface InvoicePreviewStepProps {
  invoiceData: any;
  businessDetails: any;
}

const InvoicePreviewStep: React.FC<InvoicePreviewStepProps> = ({
  invoiceData,
  businessDetails,
}) => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  
  // Always use AkMo Invoice template (ID: 1)
  const templateId = 1;
  
  // Apply AkMo template to the preview
  useEffect(() => {
    // Find the AkMo template
    const akmoTemplate = invoiceTemplateOptions.find(t => t.id === templateId);
    
    // Apply template to invoice data
    if (akmoTemplate && invoiceData) {
      invoiceData.template = akmoTemplate;
    }
  }, [invoiceData]);
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(date);
    } catch (error) {
      return dateString;
    }
  };
  
  // Calculate total amounts
  const subtotal = invoiceData.items.reduce(
    (total: number, item: any) => total + item.amount,
    0
  );
  
  let discountAmount = 0;
  if (invoiceData.discountType === 'percentage') {
    discountAmount = subtotal * (invoiceData.discountValue / 100);
  } else {
    discountAmount = invoiceData.discountValue;
  }
  
  const taxableAmount = subtotal - discountAmount;
  const taxAmount = invoiceData.taxable ? taxableAmount * (invoiceData.taxRate / 100) : 0;
  const total = taxableAmount + taxAmount;
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Invoice Preview
      </Typography>
      
      <Paper 
        ref={invoiceRef}
        elevation={0}
        variant="outlined"
        sx={{ 
          p: 4, 
          mb: 3,
          position: 'relative',
          backgroundColor: '#fff',
        }}
      >
        {/* Invoice Header */}
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* Business Details */}
          <Grid item xs={6}>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {businessDetails.businessName || businessDetails.name || 'Your Business Name'}
            </Typography>
            
            {businessDetails.address && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {businessDetails.address}
                {businessDetails.city && `, ${businessDetails.city}`}
                {businessDetails.state && `, ${businessDetails.state}`}
                {businessDetails.pincode && ` - ${businessDetails.pincode}`}
              </Typography>
            )}
            
            {businessDetails.email && (
              <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                <MailIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{businessDetails.email}</Typography>
              </Box>
            )}
            
            {businessDetails.phone && (
              <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                <PhoneIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">{businessDetails.phone}</Typography>
              </Box>
            )}
            
            {businessDetails.gstin && (
              <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                <AssignmentIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2">GSTIN: {businessDetails.gstin}</Typography>
              </Box>
            )}
          </Grid>
          
          {/* Invoice Details */}
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <Typography variant="h5" fontWeight="bold" color="primary">
              INVOICE
            </Typography>
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Invoice #:
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {invoiceData.invoiceNumber}
              </Typography>
            </Box>
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Date:
              </Typography>
              <Typography variant="body1">
                {formatDate(invoiceData.invoiceDate)}
              </Typography>
            </Box>
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Due Date:
              </Typography>
              <Typography variant="body1">
                {formatDate(invoiceData.dueDate)}
              </Typography>
            </Box>
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Status:
              </Typography>
              <Chip 
                size="small" 
                label={invoiceData.status.toUpperCase()} 
                color={
                  invoiceData.status === 'paid' ? 'success' : 
                  invoiceData.status === 'pending' ? 'warning' : 
                  invoiceData.status === 'overdue' ? 'error' : 
                  'default'
                }
              />
            </Box>
          </Grid>
        </Grid>
        
        {/* Client Section */}
        <Box sx={{ mb: 4 }}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Bill To:
            </Typography>
            
            <Typography variant="body1" fontWeight="medium">
              {invoiceData.client}
            </Typography>
            
            {invoiceData.clientDetails.address && (
              <Typography variant="body2" color="text.secondary">
                {invoiceData.clientDetails.address}
              </Typography>
            )}
            
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {invoiceData.clientDetails.gstin && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    GSTIN: {invoiceData.clientDetails.gstin}
                  </Typography>
                </Grid>
              )}
              
              {invoiceData.clientDetails.email && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <MailIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {invoiceData.clientDetails.email}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {invoiceData.clientDetails.phone && (
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center">
                    <PhoneIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      {invoiceData.clientDetails.phone}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Box>
        
        {/* Items Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell width="5%">#</TableCell>
                <TableCell width="35%">Item</TableCell>
                <TableCell width="15%" align="center">Qty</TableCell>
                <TableCell width="15%" align="right">Rate</TableCell>
                <TableCell width="15%" align="right">GST</TableCell>
                <TableCell width="15%" align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceData.items.map((item: any, index: number) => (
                <TableRow key={item.id} hover>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {item.name}
                    </Typography>
                    {item.description && (
                      <Typography variant="caption" color="text.secondary">
                        {item.description}
                      </Typography>
                    )}
                    {item.hsnCode && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        HSN: {item.hsnCode}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="right">{formatCurrency(item.rate)}</TableCell>
                  <TableCell align="right">{item.gstRate || invoiceData.taxRate}%</TableCell>
                  <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Totals */}
        <Grid container spacing={3}>
          {/* Notes and T&C */}
          <Grid item xs={12} md={6}>
            {(invoiceData.notes || invoiceData.termsAndConditions) && (
              <Box sx={{ mb: 3 }}>
                {invoiceData.notes && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Notes:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {invoiceData.notes}
                    </Typography>
                  </Box>
                )}
                
                {invoiceData.termsAndConditions && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Terms & Conditions:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {invoiceData.termsAndConditions}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
          
          {/* Invoice Totals */}
          <Grid item xs={12} md={6}>
            <Box sx={{ backgroundColor: '#f9f9f9', p: 2, borderRadius: 1, ml: 'auto', maxWidth: 300 }}>
              <Grid container spacing={1}>
                <Grid item xs={7}>
                  <Typography variant="body2">Subtotal:</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="body2" align="right">{formatCurrency(subtotal)}</Typography>
                </Grid>
                
                {discountAmount > 0 && (
                  <>
                    <Grid item xs={7}>
                      <Typography variant="body2">
                        Discount 
                        {invoiceData.discountType === 'percentage' && 
                         ` (${invoiceData.discountValue}%)`}:
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="body2" align="right">
                        -{formatCurrency(discountAmount)}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                {invoiceData.taxable && (
                  <>
                    <Grid item xs={7}>
                      <Typography variant="body2">
                        {invoiceData.taxType.toUpperCase()} ({invoiceData.taxRate}%):
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="body2" align="right">
                        {formatCurrency(taxAmount)}
                      </Typography>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={7}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Total:
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="subtitle2" fontWeight="bold" align="right">
                    {formatCurrency(total)}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="caption" align="right" display="block" sx={{ mt: 1 }}>
                    {total > 0
                      ? `Amount in words: ${amountInWords(total)} only`
                      : ''}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        
        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Thank you for your business!
          </Typography>
        </Box>
      </Paper>
      
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          You can save, print, download or email this invoice from the bottom toolbar.
        </Typography>
      </Box>
    </Box>
  );
};

// Helper function to convert amount to words
function amountInWords(amount: number): string {
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  function convertLessThanOneThousand(n: number): string {
    if (n === 0) return '';
    
    if (n < 10) return units[n];
    
    if (n < 20) return teens[n - 10];
    
    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + units[n % 10] : '');
    }
    
    return units[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanOneThousand(n % 100) : '');
  }
  
  function convert(n: number): string {
    if (n === 0) return 'Zero';
    
    let result = '';
    
    // Handle crores (10 million)
    if (n >= 10000000) {
      result += convertLessThanOneThousand(Math.floor(n / 10000000)) + ' Crore ';
      n %= 10000000;
    }
    
    // Handle lakhs (100 thousand)
    if (n >= 100000) {
      result += convertLessThanOneThousand(Math.floor(n / 100000)) + ' Lakh ';
      n %= 100000;
    }
    
    // Handle thousands
    if (n >= 1000) {
      result += convertLessThanOneThousand(Math.floor(n / 1000)) + ' Thousand ';
      n %= 1000;
    }
    
    // Handle hundreds and rest
    if (n > 0) {
      result += convertLessThanOneThousand(n);
    }
    
    return result.trim();
  }
  
  // Split the amount into rupees and paise
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  
  let result = convert(rupees) + ' Rupees';
  
  if (paise > 0) {
    result += ' and ' + convert(paise) + ' Paise';
  }
  
  return result;
}

export default InvoicePreviewStep; 